const DEFAULT_API_BASE_URL = "https://api.solusiiotcerdas.id/api";

const SITE_SLUG_ALIASES = {
  "solusi-iot-cerdas": "solusi-iot-cerdas",
  "solusi_iot_cerdas": "solusi-iot-cerdas",
  "solusiiotcerdas.id": "solusi-iot-cerdas",
  "www.solusiiotcerdas.id": "solusi-iot-cerdas",

  "bukuoibook": "bukuoibook",
  "bukuoibook.com": "bukuoibook",
  "www.bukuoibook.com": "bukuoibook",

  "alpukat-miki": "alpukat-miki",
  "alpukatmikicipedak.my.id": "alpukat-miki",
  "www.alpukatmikicipedak.my.id": "alpukat-miki",

  "depotaircerdas": "depotaircerdas",
  "depot-air": "depotaircerdas",
  "depot-air-cerdas": "depotaircerdas",
  "depotaircerdas.com": "depotaircerdas",
  "www.depotaircerdas.com": "depotaircerdas",
};

export const STORAGE_KEYS = {
  token: "sic_auth_token",
  refresh: "sic_auth_refresh",
  user: "sic_auth_user",
  siteSlug: "sic_site_slug",
  siteSettings: "sic_site_settings",
};

function slugifyLoose(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\/$/, "")
    .replace(/[^a-z0-9-]/g, "");
}

export function inferSiteSlugFromHostname(hostname) {
  const host = String(hostname || "")
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/\/$/, "");

  if (!host) return "";

  if (SITE_SLUG_ALIASES[host]) {
    return SITE_SLUG_ALIASES[host];
  }

  const stripped = host.replace(/^www\./, "");
  if (SITE_SLUG_ALIASES[stripped]) {
    return SITE_SLUG_ALIASES[stripped];
  }

  const firstLabel = stripped.split(".")[0] || "";
  if (SITE_SLUG_ALIASES[firstLabel]) {
    return SITE_SLUG_ALIASES[firstLabel];
  }

  return slugifyLoose(firstLabel);
}

export function normalizeSiteSlug(siteSlug) {
  const raw = String(siteSlug || "").trim().toLowerCase();
  if (!raw) return "";

  if (SITE_SLUG_ALIASES[raw]) {
    return SITE_SLUG_ALIASES[raw];
  }

  if (raw.includes(".") || raw.includes("http://") || raw.includes("https://")) {
    const inferred = inferSiteSlugFromHostname(raw);
    if (inferred) return inferred;
  }

  return slugifyLoose(raw);
}

const PINNED_SITE_SLUG = normalizeSiteSlug(process.env.REACT_APP_SITE_SLUG);

const DEFAULT_SITE_SLUG =
  PINNED_SITE_SLUG ||
  inferSiteSlugFromHostname(typeof window !== "undefined" ? window.location.hostname : "") ||
  "solusi-iot-cerdas";

export function getApiBaseUrl() {
  const envUrl = process.env.REACT_APP_API_URL || "";
  return (envUrl || DEFAULT_API_BASE_URL).replace(/\/+$/, "");
}

export function getSiteSlug() {
  try {
    if (PINNED_SITE_SLUG) return PINNED_SITE_SLUG;

    const saved = localStorage.getItem(STORAGE_KEYS.siteSlug);
    if (saved) return normalizeSiteSlug(saved);

    return DEFAULT_SITE_SLUG;
  } catch (error) {
    console.error("Gagal membaca site slug:", error);
    return DEFAULT_SITE_SLUG;
  }
}

export function setSiteSlug(siteSlug) {
  try {
    const normalized = normalizeSiteSlug(siteSlug);

    if (PINNED_SITE_SLUG) {
      localStorage.setItem(STORAGE_KEYS.siteSlug, PINNED_SITE_SLUG);
      return PINNED_SITE_SLUG;
    }

    if (normalized) {
      localStorage.setItem(STORAGE_KEYS.siteSlug, normalized);
      return normalized;
    }

    localStorage.removeItem(STORAGE_KEYS.siteSlug);
    return "";
  } catch (error) {
    console.error("Gagal menyimpan site slug:", error);
    return "";
  }
}

export function clearSiteCache() {
  try {
    localStorage.removeItem(STORAGE_KEYS.siteSlug);
    localStorage.removeItem(STORAGE_KEYS.siteSettings);
  } catch (error) {
    console.error("Gagal membersihkan site cache:", error);
  }
}

export function getAuthToken() {
  try {
    return localStorage.getItem(STORAGE_KEYS.token) || "";
  } catch (error) {
    console.error("Gagal membaca auth token:", error);
    return "";
  }
}

export function setAuthToken(token) {
  try {
    if (token) {
      localStorage.setItem(STORAGE_KEYS.token, token);
    } else {
      localStorage.removeItem(STORAGE_KEYS.token);
    }
  } catch (error) {
    console.error("Gagal menyimpan auth token:", error);
  }
}

export function getRefreshToken() {
  try {
    return localStorage.getItem(STORAGE_KEYS.refresh) || "";
  } catch (error) {
    console.error("Gagal membaca refresh token:", error);
    return "";
  }
}

export function setRefreshToken(token) {
  try {
    if (token) {
      localStorage.setItem(STORAGE_KEYS.refresh, token);
    } else {
      localStorage.removeItem(STORAGE_KEYS.refresh);
    }
  } catch (error) {
    console.error("Gagal menyimpan refresh token:", error);
  }
}

export function clearAuthStorage() {
  try {
    localStorage.removeItem(STORAGE_KEYS.token);
    localStorage.removeItem(STORAGE_KEYS.refresh);
    localStorage.removeItem(STORAGE_KEYS.user);
  } catch (error) {
    console.error("Gagal menghapus auth storage:", error);
  }
}

function buildQueryString(params = {}) {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null && item !== "") {
          searchParams.append(key, String(item));
        }
      });
      return;
    }

    searchParams.append(key, String(value));
  });

  const qs = searchParams.toString();
  return qs ? `?${qs}` : "";
}

function buildUrl(endpoint, params = {}) {
  const normalizedEndpoint = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
  return `${getApiBaseUrl()}${normalizedEndpoint}${buildQueryString(params)}`;
}

function buildHeaders(customHeaders = {}, options = {}) {
  const token = getAuthToken();
  const siteSlug = options.omitSiteHeader
    ? ""
    : normalizeSiteSlug(options.siteSlug || getSiteSlug());

  const headers = {
    Accept: "application/json",
    ...customHeaders,
  };

  if (siteSlug) {
    headers["X-Site-Slug"] = siteSlug;
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

async function parseResponse(response) {
  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return response.json();
  }
  return response.text();
}

async function handleResponse(response) {
  const data = await parseResponse(response);

  if (!response.ok) {
    const message =
      (typeof data === "object" && (data?.message || data?.detail || data?.error)) ||
      `HTTP ${response.status} ${response.statusText}`;

    const error = new Error(message);
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
}

export async function request(endpoint, options = {}) {
  const {
    method = "GET",
    params,
    body,
    headers = {},
    siteSlug,
    isFormData = false,
    omitSiteHeader = false,
  } = options;

  const finalHeaders = buildHeaders(headers, { siteSlug, omitSiteHeader });

  if (!isFormData) {
    finalHeaders["Content-Type"] = "application/json";
  }

  const response = await fetch(buildUrl(endpoint, params), {
    method,
    headers: finalHeaders,
    body:
      body === undefined || body === null
        ? undefined
        : isFormData
        ? body
        : JSON.stringify(body),
  });

  return handleResponse(response);
}

export function apiGet(endpoint, params = {}, options = {}) {
  return request(endpoint, { ...options, method: "GET", params });
}

export function apiPost(endpoint, body = {}, options = {}) {
  return request(endpoint, { ...options, method: "POST", body });
}

export function apiPut(endpoint, body = {}, options = {}) {
  return request(endpoint, { ...options, method: "PUT", body });
}

export function apiPatch(endpoint, body = {}, options = {}) {
  return request(endpoint, { ...options, method: "PATCH", body });
}

export function apiDelete(endpoint, body = null, options = {}) {
  return request(endpoint, { ...options, method: "DELETE", body });
}

export function apiUpload(endpoint, formData, options = {}) {
  return request(endpoint, {
    ...options,
    method: "POST",
    body: formData,
    isFormData: true,
  });
}